import { SectionHeader } from '../common/section-header'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { OperatorFaqBadgeTrigger } from '../operator-faq/operator-faq'
import { ATTENTION_LABEL } from './student-attention'
import { LIFECYCLE_LABEL } from './student-lifecycle'
import type { AttentionReason, LifecycleStage, StudentRow } from './types'

export function AttentionCell({
  reasons,
  className,
}: {
  reasons: AttentionReason[]
  className?: string
}): JSX.Element {
  if (reasons.length === 0) return <span className="text-text-secondary">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((reason) => (
        <AttentionBadge key={reason} reason={reason} className={className} />
      ))}
    </div>
  )
}

export function AttentionBadge({
  reason,
  className,
}: {
  reason: AttentionReason
  className?: string
}): JSX.Element {
  return (
    <OperatorFaqBadgeTrigger
      topicId={
        reason === 'continuity_delayed'
          ? 'continuity-waiting'
          : 'student-attention'
      }
      label={ATTENTION_LABEL[reason]}
      variant="danger"
      className={className}
    />
  )
}

export function LifecycleBadge({
  stage,
  className,
}: {
  stage: LifecycleStage
  className?: string
}): JSX.Element {
  return (
    <OperatorFaqBadgeTrigger
      topicId="student-lifecycle"
      label={LIFECYCLE_LABEL[stage]}
      variant="neutral"
      className={className}
    />
  )
}

export function StudentStatusPanel({
  student,
  createdLabel,
  lastPracticeLabel,
  notice,
}: {
  student: StudentRow
  createdLabel: string
  lastPracticeLabel: string
  notice?: ReactNode
}): JSX.Element {
  return (
    <section
      aria-label="Student overview"
      className="rounded-token-card bg-surface-card px-6 py-4"
    >
      <SectionHeader
        title="Student overview"
        meta={<section
          aria-label="Lifecycle and attention"
          className="flex min-w-0 flex-wrap items-center gap-2"
        >
          <LifecycleBadge stage={student.lifecycleStage} />
          {student.attentionReasons.length > 0 && (
            <ul className="flex min-w-0 flex-wrap gap-1 text-body">
              {student.attentionReasons.map((reason) => (
                <li key={reason}>
                  <AttentionBadge reason={reason} />
                </li>
              ))}
            </ul>
          )}
        </section>}
      />

      {notice !== undefined && <div className="mt-4">{notice}</div>}

      <dl className="mt-4 grid gap-x-8 gap-y-3 text-body sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-text-secondary">Email</dt>
          <dd className="break-all">{student.email}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-text-secondary">Client</dt>
          <dd>
            <Link
              to={`/admin/clients/${student.clientId}`}
              className="underline hover:text-text-primary"
            >
              {student.clientName}
            </Link>
          </dd>
        </div>
        <div>
          <dt className="text-text-secondary">Created</dt>
          <dd>{createdLabel}</dd>
        </div>
        <div>
          <dt className="text-text-secondary">Last practice</dt>
          <dd>{lastPracticeLabel}</dd>
        </div>
      </dl>
    </section>
  )
}
